import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { LocalStorageService } from '../storage/local-storage.service';
import { Watchlist, WatchlistItem, WatchlistResponse } from '../../domain/public-api';
import { UtilityService } from '../../utils/utility.service';
import { WatchlistRestService } from './watchlist-rest.service';
import { WatchlistUtils } from './watchlist-utils';

/**
 * Watchlist Service.
 */
@Injectable({
  providedIn: 'root'
})
export class WatchlistService {

  public static readonly WATCHLIST_SINGLE_ROUTE: string = 'modal.watchlist.single';
  public static readonly WATCHLIST_MULTI_ROUTE: string = 'modal.watchlist.multi';
  public static readonly PAGE_WATCHLIST_SINGLE_ROUTE: string = 'page.watchlist.single';
  public static readonly ROUTE_STATE_KEY: string = 'watchlistRouteState';
  public static readonly SELECTED_WATCHLIST_ID_KEY: string = 'selectedWatchlistId';

  readonly watchlistChange: Subject<void>;
  readonly watchlistIdChange: Subject<number>;

  constructor(private localStorageService: LocalStorageService,
              private translateService: TranslateService,
              private watchlistRestService: WatchlistRestService) {
    this.watchlistChange = new Subject<void>();
    this.watchlistIdChange = new Subject<number>();
  }

  setWatchlistRouteState(state: string): void {
    return this.localStorageService.setItem(WatchlistService.ROUTE_STATE_KEY, state);
  }

  setWatchlistId(id: string): void {
    // do not store recent quotes tab ID: 0
    if (id !== undefined && Number(id) > 0) {
      const storedWatchlistId: string = this.localStorageService.getItem<string>(WatchlistService.SELECTED_WATCHLIST_ID_KEY);
      if (id !== storedWatchlistId) {
        this.localStorageService.setItem(WatchlistService.SELECTED_WATCHLIST_ID_KEY, id);
        this.watchlistIdChange.next(Number(id));
      }
    }
  }

  getWatchlistId(allWatchlists: Array<Watchlist>): string {
    const storedWatchlistId: string = this.localStorageService.getItem<string>(WatchlistService.SELECTED_WATCHLIST_ID_KEY);
    let watchlistNumber: string;
    if (storedWatchlistId === null || storedWatchlistId === undefined) {
      watchlistNumber = WatchlistUtils.hasWatchlists(allWatchlists) ? allWatchlists[0].number.toString() : '1';
      this.setWatchlistId(watchlistNumber);
    } else {
      // local storage returns numeric values as numbers (typeof storedWatchlistId === "number"), have to force it back to string
      watchlistNumber = storedWatchlistId.toString();
    }
    return watchlistNumber;
  }

  getAllWatchlists(includeQuotes: boolean): Observable<Array<Watchlist>> {
    return this.watchlistRestService.getAllWatchlists(includeQuotes);
  }

  getWatchlistById(id: number): Observable<Watchlist> {
    if (id === 0) {
      throw new Error('trying to get watchlist with ID:0, id=' + id);
    }
    return this.watchlistRestService.getWatchlistById(id);
  }

  getWatchlistWithFundamentalsById(id: number): Observable<Watchlist> {
    return this.watchlistRestService.getWatchlistWithFundamentalsById(id);
  }

  saveWatchlistName(watchlistId: number, watchlistName: string): Observable<void> {

    return this.watchlistRestService.saveWatchlist({ number: watchlistId, name: watchlistName } as WatchlistResponse)
      .pipe(tap(() => this.watchlistChange.next()));
  }

  /**
   * Delete a given item from the watchlist and persist the modified watchlist.
   *
   * @param itemToDelete to delete
   * @param watchlist container from which the items are to be deleted
   */
  deleteWatchlistItem(itemToDelete: WatchlistItem, watchlist: Watchlist): Observable<void> {
    const watchlistToSave: Watchlist = UtilityService.deepClone(watchlist);
    WatchlistUtils.removeItemFromWatchlist(watchlistToSave, itemToDelete);
    return this.saveCurrentWatchlist(watchlistToSave, watchlist);
  }

  saveCurrentWatchlist(watchlistToSave: Watchlist, watchlist: Watchlist): Observable<void> {
    const holdingsInCurrentWatchlist = WatchlistUtils.getNumberOfWatchlistItems(watchlistToSave);
    if (holdingsInCurrentWatchlist === 0) {
      return this.watchlistRestService.deleteWatchlist(watchlist)
        .pipe(tap(() => this.watchlistChange.next()));
    } else if (holdingsInCurrentWatchlist <= WatchlistUtils.MAX_WATCHLIST_ENTRIES) {
      return this.saveWatchlist(watchlistToSave);
    }
  }

  saveWatchlist(watchlist: Watchlist): Observable<void> {
    this.localizeNameForSingleWatchlist(watchlist);
    const watchlistToSave: WatchlistResponse = WatchlistUtils.toWatchlistResponse(watchlist);
    return this.watchlistRestService.saveWatchlist(watchlistToSave)
      .pipe(tap(() => this.watchlistChange.next()));
  }

  /**
   * Adds default name to a given watchlist.
   *
   * @param watchlist Watchlist
   * @returns Watchlist with name added
   */
  localizeNameForSingleWatchlist(watchlist: Watchlist): Watchlist {
    if (watchlist.phantom === true || watchlist.name === undefined || watchlist.name.trim().length === 0) {
      watchlist.name = this.translateService.instant('tdWb.watchlist.title.GENERIC_TITLE') + this.lpad(watchlist.number);
    } else if (watchlist.name !== undefined) {
      watchlist.name = watchlist.name.trim();
    }
    return watchlist;
  }

  /**
   * Converts a watchlist number into a string and prepends it with zeros.
   */
  private lpad(value: number): string {
    const str = value.toString();
    const chars = ' 00';
    return chars.substring(0, chars.length - str.length) + str;
  }
}
